import {
  createContext,
  memo,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
  useRef,
  Dispatch,
  SetStateAction,
  useEffect,
  useCallback,
} from 'react';
import { ResourceContextProvider, useDataProvider, useNotify } from 'react-admin';
import { useQuery, useInfiniteQuery, InfiniteData } from '@tanstack/react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, SelectChangeEvent } from '@mui/material';
import {
  BloxCustomDataProviderType,
  SyndicationAvailableChannelsResult,
} from 'utility/types/dataProvider';
import { CosmosEditorialContextProvider } from 'providers/contexts/CosmosEditorialContext';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { InputChoice } from 'components/Inputs/Dropdown/dropdownConsts';
import { SearchContextProvider } from 'components/Search/SearchContextProvider';
import { useSavedSearch } from 'components/SavedSearch/SavedSearchContextProvider';
import { WireSearchView } from './WireSearchView';
import { NoAccess } from 'components/NoAccess/NoAccess';
import {
  checkSiteAdminPermission,
  checkHasAnySyndicationPermission,
} from 'utility/permissions';
import { SavedSearchContextProvider } from 'components/SavedSearch/SavedSearchContextProvider';
import { DefaultFilterSort } from 'components/Search/hooks';
import { formatFieldToLabel, buildSavedSearchParams } from 'components/Search/functions';
import { flushSync } from 'react-dom';
import { SavedSearchItem } from 'providers/contexts/extensions/SavedSearches';

export interface ChannelOptionsType {
  name: string;
  label: string;
  type: string;
  limit?: number;
  multiple?: boolean;
  options?: InputChoice[];
  default?: string | undefined;
  min?: string;
  max?: string;
  step?: string;
}

export interface ChannelsType extends InputChoice {
  driver: string;
}

export interface WireSearchContextType {
  channels: ChannelsType[];
  selectedChannel: string | undefined;
  channelOptions: ChannelOptionsType[] | undefined;
  channelsLoading: boolean;
  channelsFetching: boolean;
  channelConfigLoading: boolean;
  channelConfigFetching: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: ReturnType<typeof useInfiniteQuery>['fetchNextPage'];
  setSelectedChannel: (channel: string) => void;
  setChannels: Dispatch<SetStateAction<ChannelsType[]>>; // Page number setter
  handleChangeChannel: (event: SelectChangeEvent) => Promise<void>;
  handleQuickFitlerChange: (filterData: SavedSearchItem) => Promise<boolean>;
}

// Function to get enumeration or allowed types based on the label
export const getWireOptions = (data: any[] = []) => {
  let options: any[] = [];
  return data
    .map((item) => {
      switch (item.type) {
        case 'string':
          switch (item.name) {
            /**
             * CMSADMIN-1221
             * We had a case to convert raw_query to query for better UX,
             * but this is causing issues with some channels that expect raw_query
             * to be set as a default parameter in the channel config (e.g. YouTube),
             * so we are going to just return the raw_query parameter  until syndication
             * fixes this on their end by either removing raw_query as a default param
             * or adding query as an alias for raw_query
             */
            case 'page':
              return null;
            default:
              return {
                name: item.name,
                label: item.label,
                type: item.type,
              };
          }
        case 'asset_type':
        case 'enumeration':
          switch (item.name) {
            case 'dir':
              return null;
            case 'sort':
              options = item?.config?.options
                .map((optionItem: any) => {
                  return {
                    label: optionItem.label,
                    field: optionItem.value,
                  };
                })
                .filter((optionItem: any) => optionItem !== null);

              return {
                name: item.name,
                label: item.label,
                type: 'sort',
                options: options,
              };
            //Special override for YouTube channels
            case 'safeSearch':
              options = item?.config?.options
                .map((optionItem: any) => {
                  return {
                    label:
                      optionItem.label === 'moderate (default)'
                        ? 'moderate'
                        : optionItem.label,
                    value: String(optionItem.value),
                  };
                })
                .filter((optionItem: any) => optionItem !== null);
              return {
                name: item.name,
                label: item.label,
                type: 'select',
                options: options,
                default: item?.default,
              };
            default:
              const configOptions =
                item?.config?.options || item?.config['allowed-types'];
              options = configOptions
                .map((optionItem: any) => {
                  if (typeof optionItem === 'string') {
                    return {
                      label: optionItem,
                      value: optionItem,
                    };
                  } else if (
                    typeof optionItem === 'object' &&
                    optionItem.label &&
                    optionItem.value != null
                  ) {
                    return {
                      label: optionItem.label,
                      value: String(optionItem.value),
                    };
                  } else {
                    return null;
                  }
                })
                .filter((optionItem: any) => optionItem !== null) as InputChoice[];

              return {
                name: item.name,
                label: item.label,
                type: 'select',
                multiple: item?.config?.multiple,
                options: options,
                default: item?.default ?? undefined,
              };
          }
        case 'daterange':
          return {
            name: item.name,
            label: item.label,
            type: item.type,
          };
        case 'datetime':
          return {
            name: item.name,
            label: item.label,
            type: item.type,
          };
        case 'number':
        case 'numberrange':
          switch (item.name) {
            case 'limit':
              return {
                name: item.name,
                label: item.label,
                type: item.type,
                limit: item?.config?.max,
              };
            default:
              return {
                name: item.name,
                label: item.label,
                type: item.type,
                min: item?.config?.min,
                max: item?.config?.max,
                step: item?.config?.step,
              };
          }
        default:
          return null;
      }
    })
    .filter((item) => item !== null) as ChannelOptionsType[];
};

/**
 * Get default query parameters for wire search
 * as each channel may have its own default qp and
 * filter/sort options. Returns an object
 * with the default qp to be set for the search.
 */
export const getWireChannelDefaultQp = (
  channelOptions?: ChannelOptionsType[],
  selectedChannel?: string,
): DefaultFilterSort => {
  let defaultFilterSort: DefaultFilterSort = {};

  if (selectedChannel && channelOptions && Array.isArray(channelOptions)) {
    defaultFilterSort = {
      channel_id: selectedChannel,
      ...channelOptions
        ?.filter((option: ChannelOptionsType) => option.default !== undefined)
        .reduce((acc: { [key: string]: any }, option: ChannelOptionsType) => {
          acc[option.name] = option.default;
          return acc;
        }, {}),
    };

    const sortOptions = channelOptions.find(
      (option: ChannelOptionsType) => option.name === 'sort',
    )?.options;

    if (sortOptions && sortOptions.length > 0) {
      const options = sortOptions.map((option) => ({
        field: option.field,
        label: formatFieldToLabel(option.field),
      }));
      defaultFilterSort.sort = options[0].field;
      defaultFilterSort.dir = 'DESC';
    }
  }

  return defaultFilterSort;
};

// ---------- WIRE SEARCH CONTEXT PROVIDER  ---------- //
export const WireSearchContext = createContext<WireSearchContextType | undefined>(
  undefined,
);

export const useWireSearch = () => {
  const context = useContext(WireSearchContext);
  if (!context) {
    throw new Error('useWireSearch must be used within an WireSearchProvider');
  }
  return context;
};

export const WireSearchContextProvider = memo(({ children }: PropsWithChildren) => {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const notify = useNotify();
  const navigate = useNavigate();
  const location = useLocation();
  const [channels, setChannels] = useState<ChannelsType[]>([]);
  const hasChannels = Boolean(channels && channels.length > 0);
  const [queryParams, updateQueryParams] = useSearchParams();
  const [selectedChannel, setSelectedChannel] = useState<string | undefined>(
    new URLSearchParams(location.search)?.get('channel_id') || undefined,
  );

  /** Tracks the most-recently started channel/config request. */
  const latestChannelRequestRef = useRef(0);

  const expectedUrlChannelRef = useRef<string | undefined>(undefined);

  /** Tracks an in-flight default query param / saved search application. */
  const applyingDefaultQpRef = useRef(false);

  /**
   * Derive channel_id from the live URL params so we can sync
   * selectedChannel state when the URL changes externally
   * (e.g. navigating back to All Feeds with no channel_id).
   */
  const urlChannelId = queryParams.get('channel_id') || undefined;
  const { defaultSavedSearchState, updateQuickFilterValue } = useSavedSearch();

  const [channelOptions, setChannelOptions] = useState<ChannelOptionsType[] | undefined>(
    undefined,
  );

  const {
    data: channelsData,
    isLoading: channelsLoading,
    isFetching: channelsFetching,
    isFetchingNextPage,
    status: channelsStatus,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<
    SyndicationAvailableChannelsResult,
    Error,
    InfiniteData<SyndicationAvailableChannelsResult>,
    string[],
    string | number
  >({
    queryKey: ['syndicationChannelsQueryKey', 'getAvailableChannels'],
    queryFn: async ({ pageParam }) => {
      try {
        const response = await dataProvider.getAvailableChannels(pageParam || undefined);
        if (response.status !== 'success') {
          throw new Error(`API responded with: ${JSON.stringify(response, null, 2)}`);
        }
        return response;
      } catch (error) {
        console.error('[getAvailableChannels] Error:', error);
        throw error;
      }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.data.next ?? undefined,
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Fetch the channel data, but the query will
  // only be enabled if selectedChannel exists
  const {
    data: channelConfig,
    isLoading: channelConfigLoading,
    isFetching: channelConfigFetching,
    status: channelConfigStatus,
  } = useQuery({
    queryKey: [`channelConfiguration`, 'getChannelConfigurations', selectedChannel],
    queryFn: async () => {
      return await dataProvider.getChannelConfigurations(selectedChannel as string);
    },
    enabled: Boolean(selectedChannel),
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (channelsData?.pages) {
      const newChoices = channelsData.pages.flatMap((page) =>
        page.data.items.map((item) => ({
          value: item.id,
          label: item.name,
          driver: item.driver,
        })),
      );
      // Directly replace instead of appending
      setChannels(newChoices);
    }
  }, [channelsData, setChannels]);

  /**
   * Issue 1 fix: sync selectedChannel with the URL's channel_id param.
   *
   * The component is not unmounted when navigating between Feed views,
   * so selectedChannel state would otherwise persist across navigations.
   * When the user navigates back to All Feeds (no channel_id in URL),
   * this resets selectedChannel to undefined so the first-channel effect
   * below can restore it to channels[0].
   */
  useEffect(() => {
    if (urlChannelId === expectedUrlChannelRef.current) {
      expectedUrlChannelRef.current = undefined;
    } else {
      latestChannelRequestRef.current += 1;
    }

    setSelectedChannel(urlChannelId);
    // Only re-run when the URL's channel_id value actually changes.
    // Intentionally omitting selectedChannel to avoid a feedback loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlChannelId]);

  useEffect(() => {
    if (!selectedChannel && channels.length > 0) {
      setSelectedChannel(channels[0].value);
    }
  }, [channels, selectedChannel, setSelectedChannel]);

  useEffect(() => {
    if (channelConfig?.data) {
      const options = getWireOptions(channelConfig?.data);
      setChannelOptions(options);
    }
  }, [channelConfig, setChannelOptions]);

  /**
   * When the channel changes - update in state and set channnel
   * query param. Fetch the selected channels config
   * to get and apply the channes default query parameters.
   *
   * eg: channel query param has changed so we need to
   * apply the defaut query parameters for this channel
   * once the channelOptions are fetched and available.
   */

  const handleChangeChannel = useCallback(
    async (event: SelectChangeEvent) => {
      const newChannel = event?.target?.value;

      // Stamp each invocation, including same-channel requests, so an older
      // response cannot overwrite a newer selection.
      const requestId = ++latestChannelRequestRef.current;
      expectedUrlChannelRef.current = newChannel;

      // flushsync to mark as priority
      // since qp and url need to update
      // together before we await refetch
      flushSync(() => {
        setSelectedChannel(newChannel);
        updateQueryParams({ channel_id: newChannel }, { replace: true });
      });

      // await get new config, map and set channe opts
      // and get and set the new default channel qps
      try {
        const newConfig = await dataProvider.getChannelConfigurations(newChannel);

        // Discard stale results if another channel/config request started.
        if (latestChannelRequestRef.current !== requestId) {
          return;
        }

        // update channel options
        const opts = getWireOptions(newConfig?.data ?? []);
        setChannelOptions(opts);
        // apply channel default query params
        const defaultChannelQp = getWireChannelDefaultQp(opts, newChannel);
        updateQueryParams(defaultChannelQp, { replace: true });
      } catch (err) {
        console.error('[handleChangeChannel] Error:', err);
      }
    },
    [dataProvider, setSelectedChannel, updateQueryParams],
  );

  const handleQuickFitlerChange = useCallback(
    async (filterData: SavedSearchItem) => {
      const newChannel = filterData?.payload?.channel_id || selectedChannel;

      if (!newChannel) {
        return false;
      }

      // Use a request id rather than only the channel value so two saved
      // searches for the same channel are still ordered correctly.
      const requestId = ++latestChannelRequestRef.current;
      expectedUrlChannelRef.current = newChannel;

      // flushsync to mark as priority
      // since qp and url need to update
      // together before we await refetch
      flushSync(() => {
        // update selected channel in state
        setSelectedChannel(newChannel);
      });

      // await get new config, map and set channe opts
      // and get and set the new default channel qps
      try {
        const newConfig = await dataProvider.getChannelConfigurations(newChannel);

        // Discard stale results if another channel/config request started.
        if (latestChannelRequestRef.current !== requestId) {
          return false;
        }
        // update channel options
        const opts = getWireOptions(newConfig?.data ?? []);
        setChannelOptions(opts);
        return true; // Return true to indicate success
      } catch (err) {
        console.error('[handleQuickFitlerChange] Error:', err);
        notify('Failed to change quick filter', { type: 'error' });
        return false; // Return false to indicate failure
      }
    },
    [dataProvider, selectedChannel, setSelectedChannel, notify],
  );

  /**
   * Handle application of default channels default query parameters
   *
   * apply if there are no query params
   * or if the nav state has reloadDefault signal.
   *
   * Note once first default has been loaded, the `?channel` query param
   * may always exist when swapping channels, so this effect should just
   * handle directly navigating to the search view `/feeds/search` with no query
   * params at all, or if the nav state has reloadDefault set to true.
   *
   */

  const queryParamsObject = Object.fromEntries(queryParams.entries());
  const hasNoQueryParams = Object.keys(queryParamsObject).length === 0;
  const navReloadDefault = location?.state?.reloadDefault;

  // handle applying the filter
  const applySavedSearch = useCallback(
    async (item: SavedSearchItem) => {
      const applied = await handleQuickFitlerChange(item);
      // channel changed while the config fetch was in-flight, do not
      // overwrite the URL with this now stale saved search
      if (!applied) {
        return;
      }
      // build editorial saved search filter payload
      const ssPayload = item?.payload || {};
      const newParamStr = buildSavedSearchParams({
        payload: ssPayload,
      });
      // if empty do not apply
      if (!newParamStr) {
        return;
      }
      const savedSearchParams = new URLSearchParams(newParamStr);
      const savedSearchChannel = ssPayload.channel_id || selectedChannel;
      if (savedSearchChannel && !savedSearchParams.has('channel_id')) {
        savedSearchParams.set('channel_id', savedSearchChannel);
      }
      // apply to select state, url, and track pending/applied via refs
      updateQuickFilterValue(item);
      updateQueryParams(savedSearchParams, { replace: true });
    },
    [updateQueryParams, updateQuickFilterValue, handleQuickFitlerChange, selectedChannel],
  );

  useEffect(() => {
    // wait for neccessary data to be available
    if (channelConfigStatus === 'pending' || channelsLoading) {
      return;
    }

    // no qp or requested reload via nav state
    const shouldApplyQp = navReloadDefault || hasNoQueryParams;
    // has channel and options ready
    const canApplyQp = selectedChannel && channelOptions && Array.isArray(channelOptions);

    // An apply is already awaiting its channel config fetch. Without this guard
    // the effect re-fires while the URL is still empty (channelOptions and other
    // deps settle mid-await) and queues duplicate applies that land later and
    // clobber a channel the user has since selected.
    if (applyingDefaultQpRef.current) {
      return;
    }

    if (shouldApplyQp && canApplyQp) {
      let newQp = getWireChannelDefaultQp(channelOptions, selectedChannel);
      if (
        defaultSavedSearchState.initialized &&
        defaultSavedSearchState.hasDefaultSavedSearch
      ) {
        const ssItem = defaultSavedSearchState?.defaultSavedSearch;
        if (ssItem) {
          applyingDefaultQpRef.current = true;
          applySavedSearch(ssItem).finally(() => {
            applyingDefaultQpRef.current = false;
          });
          return; // exit as applySavedSearch will handle the URL update
        }
      }
      navigate(
        `${location.pathname}?${new URLSearchParams(newQp).toString()}${location.hash}`,
        {
          state: { reloadDefault: false }, // prevent qp reload loops
        },
      );
    }
  }, [
    channelsLoading,
    channelConfigStatus,
    channelOptions,
    channelsStatus,
    hasNoQueryParams,
    location.pathname,
    navReloadDefault,
    navigate,
    selectedChannel,
    location.hash,
    defaultSavedSearchState,
    applySavedSearch,
  ]);

  const contextValue = useMemo(() => {
    return {
      channels,
      selectedChannel,
      setSelectedChannel,
      channelsLoading,
      channelsFetching,
      channelConfigLoading,
      channelConfigFetching,
      channelOptions,
      setChannels,
      handleChangeChannel,
      handleQuickFitlerChange,
      hasNextPage: hasNextPage ?? false,
      isFetchingNextPage,
      fetchNextPage,
    };
  }, [
    channels,
    selectedChannel,
    channelsLoading,
    channelsFetching,
    channelConfigLoading,
    channelConfigFetching,
    channelOptions,
    handleChangeChannel,
    handleQuickFitlerChange,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  if (channelsLoading) {
    return null;
  }

  if (!hasChannels) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        No channels are available for this site
      </Box>
    );
  }

  return (
    <WireSearchContext.Provider value={contextValue}>
      {children}
    </WireSearchContext.Provider>
  );
});

type WireSearchWithContextProps = {
  dashboard?: string;
};

export const WireSearchWithContext = ({ dashboard }: WireSearchWithContextProps) => {
  const { sessionConfig } = useCosmos();

  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: userHasSyndication, name: syndicationPermName } =
    checkHasAnySyndicationPermission(sessionConfig);
  const hasWireSyndication = Boolean(userIsAdmin || userHasSyndication);

  if (!hasWireSyndication) {
    return <NoAccess permName={syndicationPermName} />;
  }

  /**
   * Need to override the RA resource here
   * from 'feeds' to 'wire/channel/search'
   * because our client side routing is not
   * 1-to-1 with the API endpoints, and react-admin
   * creates routes based on the resource name.
   *
   * InfiniteListBase and other react-admin components
   * can take a resource prop to override the resource, but
   * since we have components that target that resource
   * above the InfiniteListBase (e.g. SearchContextProvider),
   * we need to wrap the entire search view in a
   * ResourceContextProvider to ensure that the
   * resources are matched up properly above and below the
   * actual InfiniteListBase component.
   *
   * This is to be able to properly use the helpers like
   * getSearchItemInfo, getResourceDetails, determineUniqueId, etc...
   */

  return (
    <CosmosEditorialContextProvider dashboard={dashboard}>
      <ResourceContextProvider value="wire/channel/search">
        <SavedSearchContextProvider app="wire">
          <WireSearchContextProvider>
            <SearchContextProvider>
              <WireSearchView />
            </SearchContextProvider>
          </WireSearchContextProvider>
        </SavedSearchContextProvider>
      </ResourceContextProvider>
    </CosmosEditorialContextProvider>
  );
};

export default WireSearchWithContext;
